/**
 * @author      yontaek@zumper.com (Yon Taek Chung)
 * @copyright   Copyright (c) 2025, Zumper
 * @description ZoeHeaderPopover
 */

import { MapPin, ZoeSparkle } from '@zcode-ui/icons'
import {
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
  ToggleGroup,
  ToggleGroupItem,
  useDisclosure,
} from '@zcode-ui/shadz-ui'
import {
  SEARCH_BAR_CLICK,
  ZOE_ENTRY_SEARCH_BAR_CLICK,
  ZOE_TOGGLE_CLICK,
} from '@zcode-ui/web-zumper-integrations'
import type { AutocompleteApiResult, AutocompleteResult } from '@zcode/bizos'
import { EVENT_NAMES, PAGE_NAMES, PROP_NAMES } from '@zcode/mixpanel'
import type { Origin } from '@zcode/nr-utils'
import { SharedAutocomplete } from '@zcode/nr/es/shared/components/Autocomplete/SharedAutocomplete'
import type { AIAssistantFiltersResponse, Dispatch, GetState } from '@zcode/state-type'
import React, { useState } from 'react'
import { useDispatch } from 'react-redux'
import { ZoeContentInput } from '../ZoeContentInput/ZoeContentInput'
import { trackMapListZoeEvent } from '../module/thunks/trackMapListZoeEvent'
import theme from './autocomplete.module.scss'

interface Props {
  dispatchMixpanelAutocompleteResultClick: ({
    result,
    index,
  }: {
    result: AutocompleteApiResult
    index: number
  }) => void
  getAIAssistantSearchFilters: (
    searchQuery: string
  ) => (dispatch: Dispatch, getState: GetState) => Promise<AIAssistantFiltersResponse | undefined>
  handleSearchButtonClick: (
    e: React.MouseEvent<HTMLButtonElement>,
    hoveredIndex: number,
    searchTerm: string
  ) => void
  mixpanelEvent?: (
    event: (typeof EVENT_NAMES)[keyof typeof EVENT_NAMES],
    origin: Origin | undefined,
    extraPayload?: object
  ) => (dispatch: Dispatch, getState: GetState) => void
  onCloseClick?: () => void
  onSelectAutocompleteResult: (result: AutocompleteResult) => Promise<void>
  placeholder: string
  variation: string | undefined | null
}

export const ZoeHeaderPopover = ({
  dispatchMixpanelAutocompleteResultClick,
  getAIAssistantSearchFilters,
  handleSearchButtonClick,
  mixpanelEvent,
  onCloseClick,
  onSelectAutocompleteResult,
  placeholder,
  variation,
}: Props) => {
  const dispatch = useDispatch<Dispatch>()
  const { isOpen, onOpen, onClose } = useDisclosure()
  const defaultToggle = variation === 'SHOW_ZOE_AUTOCOMPLETE_DEFAULT' ? 'zoe' : 'location'
  const [selectedToggle, setSelectedToggle] = useState(defaultToggle)

  const handleClose = () => {
    onCloseClick?.()
    onClose()
    setTimeout(() => {
      setSelectedToggle(defaultToggle)
    }, 500)
  }

  const handleAutocompleteResultClick = (result: AutocompleteResult) => {
    onSelectAutocompleteResult(result)
    handleClose()
  }

  const trackToggleClick = (value: 'zoe' | 'location') => {
    if (value === 'location') {
      dispatch(
        trackMapListZoeEvent({
          eventName: SEARCH_BAR_CLICK,
          additionalProps: {
            pageType: PAGE_NAMES.SEARCH,
          },
        })
      )
    }

    dispatch(
      trackMapListZoeEvent({
        eventName: ZOE_TOGGLE_CLICK,
        additionalProps: {
          property: value === 'zoe' ? 'Ask Zoe on Maplist' : 'Location on Maplist',
        },
      })
    )
    mixpanelEvent &&
      dispatch(
        mixpanelEvent(EVENT_NAMES.ZOE_TOGGLE_CLICK, undefined, {
          [PROP_NAMES.BUTTON_CLICKED]:
            value === 'zoe' ? 'Ask Zoe on Maplist' : 'Location on Maplist',
        })
      )

    setSelectedToggle(value)
  }

  const handleClick = () => {
    dispatch(
      trackMapListZoeEvent({
        eventName: ZOE_ENTRY_SEARCH_BAR_CLICK,
        additionalProps: {
          listName: 'mapList',
        },
      })
    )
    mixpanelEvent &&
      dispatch(
        mixpanelEvent(EVENT_NAMES.CLICK, undefined, {
          [PROP_NAMES.BUTTON_CLICKED]: 'Zoe Entry Search Bar',
        })
      )
    onOpen()
  }

  return (
    <Popover
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          handleClose()
        } else {
          handleClick()
        }
      }}
    >
      <PopoverTrigger asChild>
        <Button
          className="cursor-text text-gray-300 justify-start min-w-[392px] w-[392px] text-base font-medium h-12 border-gray-150 py-3 px-6 hover:bg-gray-0 hover:text-gray-300"
          onFocus={handleClick}
          variant="outline"
        >
          What are you looking for?
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        alignOffset={-4}
        className="z-popover p-0 pt-4 overflow-hidden"
        onCloseAutoFocus={(e) => e.preventDefault()}
        sideOffset={-52}
        style={{ width: '452px' }}
      >
        <ToggleGroup
          className="gap-0 px-4 w-full"
          onValueChange={trackToggleClick}
          type="single"
          value={selectedToggle}
        >
          {variation === 'SHOW_ZOE_AUTOCOMPLETE_DEFAULT' && (
            <>
              <ToggleGroupItem
                className="gap-1 w-1/2 rounded-l-lg rounded-r-none hover:bg-gray-50 hover:text-gray-80"
                disabled={selectedToggle === 'zoe'}
                size="sm"
                value="zoe"
                variant="zumper"
              >
                <ZoeSparkle size={20} />
                <span className="font-extrabold">Ask Zoe</span>
              </ToggleGroupItem>
              <ToggleGroupItem
                className="gap-[2px] w-1/2 border-l-0 rounded-r-lg rounded-l-none hover:bg-gray-50 hover:text-gray-80 [&>svg]:!h-[14px]"
                disabled={selectedToggle === 'location'}
                size="sm"
                value="location"
                variant="zumper"
              >
                <MapPin size={12} fill={selectedToggle === 'location' ? '#fff' : '#454442'} />
                <span className="font-extrabold">Location</span>
              </ToggleGroupItem>
            </>
          )}
          {variation === 'SHOW_ZOE' && (
            <>
              <ToggleGroupItem
                className="gap-1 w-1/2 rounded-l-lg rounded-r-none hover:bg-gray-50 hover:text-gray-80"
                disabled={selectedToggle === 'location'}
                size="sm"
                value="location"
                variant="zumper"
              >
                <MapPin size={12} fill={selectedToggle === 'location' ? '#fff' : '#454442'} />
                <span className="font-extrabold">Location</span>
              </ToggleGroupItem>
              <ToggleGroupItem
                className="gap-[2px] w-1/2 border-l-0 rounded-r-lg rounded-l-none hover:bg-gray-50 hover:text-gray-80 [&>svg]:!h-[14px]"
                disabled={selectedToggle === 'zoe'}
                size="sm"
                value="zoe"
                variant="zumper"
              >
                <ZoeSparkle size={20} />
                <span className="font-extrabold">Ask Zoe</span>
              </ToggleGroupItem>
            </>
          )}
        </ToggleGroup>
        {selectedToggle === 'zoe' && (
          <ZoeContentInput
            getAIAssistantSearchFilters={getAIAssistantSearchFilters}
            handleClose={handleClose}
            isFromSearchBar
            mixpanelEvent={mixpanelEvent}
          />
        )}
        {selectedToggle === 'location' && (
          <div className="pt-4 [&>div>form]:px-4 [&>div>form>button]:right-4 [&>div>form>button]:h-14 [&>div>form>button]:w-14 [&_form_input[type=search]]:w-[350px] [&_form_input[type=search]]:py-4 [&_form_input[type=search]]:px-[14px] [&_form]:relative">
            <SharedAutocomplete
              dispatchMixpanelAutocompleteResultClick={dispatchMixpanelAutocompleteResultClick}
              forceContextualMenuOpen
              handleSearchButtonClick={handleSearchButtonClick}
              isMapListNav
              onSelectAutocompleteResult={handleAutocompleteResultClick}
              origin={'Long-term'}
              pageName={PAGE_NAMES.SEARCH_MAP}
              placeholder={placeholder}
              theme={theme}
            />
          </div>
        )}
      </PopoverContent>
    </Popover>
  )
}
